/**
 * Session Expiry Event System
 *
 * Used to communicate session expiry from API interceptors to the UI
 * so that users are properly logged out and redirected to login page.
 */

export type SessionType = 'user' | 'admin'

export interface SessionExpiryEvent {
  type: SessionType
  reason?: string
}

const SESSION_EXPIRY_EVENT = 'session:expired'

/**
 * Emit a session expiry event
 */
export function emitSessionExpiry(sessionType: SessionType, reason?: string): void {
  const event = new CustomEvent<SessionExpiryEvent>(SESSION_EXPIRY_EVENT, {
    detail: { type: sessionType, reason }
  })
  window.dispatchEvent(event)
}

/**
 * Subscribe to session expiry events
 */
export function onSessionExpiry(callback: (event: SessionExpiryEvent) => void): () => void {
  const handler = (e: Event) => {
    const customEvent = e as CustomEvent<SessionExpiryEvent>
    callback(customEvent.detail)
  }

  window.addEventListener(SESSION_EXPIRY_EVENT, handler)

  // Return unsubscribe function
  return () => {
    window.removeEventListener(SESSION_EXPIRY_EVENT, handler)
  }
}
